import { create } from 'zustand'
import { OpenPanel } from '@openpanel/web'
import type { IdentifyData, GlobalProperties } from '@/lib/analytics'

interface OpenPanelState {
  instance: OpenPanel | null
  isInitialized: boolean

  // Actions
  init: () => void
  track: (name: string, properties?: Record<string, unknown>) => void
  screenView: (path?: string, properties?: Record<string, unknown>) => void
  identify: (data: IdentifyData) => void
  clear: () => void
  reset: () => void
  setGlobalProperties: (properties: GlobalProperties) => void
  increment: (property: string, value?: number) => void
  decrement: (property: string, value?: number) => void
}

export const useOpenPanelStore = create<OpenPanelState>((set, get) => ({
  instance: null,
  isInitialized: false,

  init: () => {
    if (get().isInitialized) return

    const clientId = import.meta.env.VITE_OPENPANEL_CLIENT_ID
    const apiUrl = import.meta.env.VITE_OPENPANEL_API_URL
    const isEnabled = import.meta.env.PROD || import.meta.env.VITE_ENABLE_ANALYTICS === 'true'

    if (!clientId || !isEnabled) {
      set({ isInitialized: true })
      return
    }

    const op = new OpenPanel({
      clientId,
      apiUrl: apiUrl || undefined,
      trackScreenViews: false,
      trackOutgoingLinks: true,
      trackAttributes: true,
    })

    op.setGlobalProperties({ app: 'webapp' })

    set({ instance: op, isInitialized: true })
  },

  track: (name, properties) => {
    const { instance } = get()
    if (!instance) return
    instance.track(name, properties)
  },

  screenView: (path, properties) => {
    const { instance } = get()
    if (!instance) return
    instance.screenView(path, properties)
  },

  identify: (data) => {
    const { instance } = get()
    if (!instance) return
    instance.identify(data)
  },

  clear: () => {
    const { instance } = get()
    if (!instance) return
    instance.clear()
  },

  reset: () => {
    const { instance } = get()
    if (instance) {
      instance.clear()
    }
    set({ instance: null, isInitialized: false })
  },

  setGlobalProperties: (properties) => {
    const { instance } = get()
    if (!instance) return
    instance.setGlobalProperties(properties)
  },

  increment: (property, value = 1) => {
    const { instance } = get()
    if (!instance) return
    instance.increment({ property, value })
  },

  decrement: (property, value = 1) => {
    const { instance } = get()
    if (!instance) return
    instance.decrement({ property, value })
  },
}))
